import React, { useState } from "react";
import { App, Typography } from "antd";
import { StopOutlined } from "@ant-design/icons";

import { confirmAction } from "@components/Page";
import { TableRowActions } from "@components/Table";
import { hasAccess } from "@components/Authorization";
import { useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";
import { revokeOrganizationPublicKeyOperation } from "@api/services/identity";
import { type OrganizationPublicKey } from "@api/types";

const LABEL_REVOKE = "Revoke";
const LABEL_CONFIRM_TITLE = "Revoke this public key?";
const LABEL_CONFIRM_CONTENT = "Each client that signs its ID tokens with this key stops immediately. You cannot undo this.";
const LABEL_SUCCESS_MESSAGE = "Public key $ is revoked";

const PERMISSION_DELETE = "organization-public-keys-delete";

const STATUS_ACTIVE = "ACTIVE";

type RevokeOutput = {
  data: OrganizationPublicKey;
};

type Props = {
  publicKey: OrganizationPublicKey;
  onRevoked: (revokedPublicKey: OrganizationPublicKey) => void;
};

const OrganizationPublicKeyRowActions = ({
  publicKey,
  onRevoked,
}: Props): JSX.Element => {
  const { modal } = App.useApp();
  const {
    request,
    showSuccessMessage,
  } = useAppContext() as AppContext;

  const [ isLoading, setIsLoading ] = useState(false);

  const { id, name, status } = publicKey;

  const revokePublicKey = () => {
    setIsLoading(true);

    return request<RevokeOutput>(
      revokeOrganizationPublicKeyOperation,
      { id },
    )
      .then(({ data }) => {
        onRevoked(data);
        showSuccessMessage(LABEL_SUCCESS_MESSAGE.replace("$", `"${name}"`));
      })
      .finally(() => setIsLoading(false));
  };

  const openRevokeConfirm = () =>
    confirmAction(
      modal,
      LABEL_REVOKE,
      LABEL_CONFIRM_TITLE,
      LABEL_CONFIRM_CONTENT,
      revokePublicKey,
      "danger",
    );

  const isRevoked = status !== STATUS_ACTIVE;
  const canRevoke = hasAccess([ PERMISSION_DELETE ]);
  const shouldShowRevoke = !isRevoked && canRevoke;

  // NOTE: a revoked key has no action left, and a user without the revoke
  //       permission has none either. TableRowActions renders nothing when
  //       the menu is empty.
  const items = shouldShowRevoke
    ? [
      {
        key: "revoke",
        icon: <StopOutlined />,
        danger: true,
        label: (
          <Typography.Link onClick={openRevokeConfirm}>
            {LABEL_REVOKE}
          </Typography.Link>
        ),
      },
    ]
    : [];

  return (
    <TableRowActions items={items} isLoading={isLoading} />
  );
};

export default OrganizationPublicKeyRowActions;
